import {
  isRangeDateVFrame,
  type RangeDateVFrame,
} from '@festina/life-general-types/life'
import { dateUtils, isEmpty } from '@festina-life/core'
import {
  FormikDateInput,
  type FormikDateInputProps,
} from '@festina-life/ui/formikForms'
import get from 'lodash-es/get'

import { isFieldRequired } from '../../forms/formUtility'
import { useLifeFormikContext } from '../../forms/useLifeFormikContext'
import {
  isDontUseVFrame,
  isFixedVFrame,
} from '../../forms/yup/validation/types'
import { isVFrameWithDefaultValue } from '../../forms/yup/validation/yupValidationUtils'
import { useFieldVFrame } from '../useFieldVFrame'
import type { InputPropsFromFormik } from './types'

type Props = InputPropsFromFormik<FormikDateInputProps>

/**
 *  Date input used in life forms. This should be used in those cases where a frame exists to validate the date input values.
 */
export const LifeDateInput = ({
  name,
  existingValueConfig,
  ...props
}: Props) => {
  const { values, setFieldValue } = useLifeFormikContext()
  const frameStatus = useFieldVFrame(name)

  const fixed = isFixedVFrame(frameStatus)
  const isRequired = isFieldRequired(frameStatus)
  const value = get(values, name as string)

  const defaultValue = isVFrameWithDefaultValue(frameStatus)
    ? (frameStatus as RangeDateVFrame)?.defaultValue
    : undefined

  if (isDontUseVFrame(frameStatus)) {
    return null
  }

  const [min, max] =
    frameStatus && isRangeDateVFrame(frameStatus)
      ? [frameStatus.min, frameStatus.max]
      : [undefined, undefined]

  const validationText =
    !isEmpty(min) || !isEmpty(max)
      ? `${min ? dateUtils.dateAsStr(min) : ''}–${max ? dateUtils.dateAsStr(max) : ''}`
      : undefined

  return (
    <FormikDateInput
      name={name}
      editable={!fixed}
      value={fixed ? frameStatus.value : value}
      defaultValue={defaultValue ?? undefined}
      validationText={validationText}
      onDefaultValueClick={() => setFieldValue(name, defaultValue)}
      isRequired={isRequired}
      existingValueConfig={existingValueConfig}
      hasRightColumnAlways
      {...props}
    />
  )
}
